import React from 'react';

type ParserNode = {
    type: 'text' | 'tag';
    data?: string | null;
    name?: string;
    attribs?: Record<string, string>;
};

type ReplaceFn = (node: ParserNode) => React.ReactNode;

/**
 * A lightweight HTML parser for React to replace html-react-parser
 * which has compatibility issues with older react-scripts.
 * Uses native DOMParser to convert HTML string to React nodes.
 */
export const parseHtml = (html: string | null | undefined, replace?: ReplaceFn): React.ReactNode => {
    if (!html) return null;
    
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        const convertNode = (node: ChildNode, index: number): React.ReactNode => {
            // Handle Text nodes
            if (node.nodeType === Node.TEXT_NODE) {
                if (replace) {
                    const domNodeForReplacer: ParserNode = {
                        type: 'text',
                        data: node.textContent
                    };
                    const replacement = replace(domNodeForReplacer);
                    if (replacement) return replacement;
                }
                return node.textContent;
            }
            
            // Handle Elements
            if (node.nodeType === Node.ELEMENT_NODE) {
                // Check replacement first
                // Reconstruct a simple object compliant with what html-react-parser provided if needed
                // or just pass the DOM node itself
                if (replace) {
                    // Create a simplified node object for the replacer
                    const domNodeForReplacer: ParserNode = {
                        type: 'tag',
                        name: (node as Element).tagName.toLowerCase(),
                        attribs: Array.from((node as Element).attributes).reduce<Record<string, string>>((acc, attr) => {
                            acc[attr.name] = attr.value;
                            return acc;
                        }, {})
                    };
                    
                    const replacement = replace(domNodeForReplacer);
                    if (React.isValidElement(replacement)) {
                        return React.cloneElement(replacement, { key: index });
                    }
                    if (replacement) return replacement;
                }

                const props: Record<string, unknown> = { key: index };
                
                // Attributes
                Array.from((node as Element).attributes).forEach(attr => {
                    let name = attr.name;
                    if (name === 'class') name = 'className';
                    if (name === 'for') name = 'htmlFor';
                    // Style parsing could be added here if needed, but tiptapRenderer doesn't use inline styles
                    props[name] = attr.value;
                });

                const children = Array.from((node as Element).childNodes).map(convertNode);
                const tagName = (node as Element).tagName.toLowerCase();
                
                // Void elements
                const voidElements = ['img', 'br', 'hr', 'area', 'base', 'basefont', 'input', 'link', 'meta'];
                if (voidElements.includes(tagName)) {
                     return React.createElement(tagName, props);
                }

                return React.createElement(tagName, props, children.length > 0 ? children : null);
            }
            
            return null;
        };

        const result = Array.from(doc.body.childNodes).map(convertNode);
        // If single element, return it, else fragment (array)
        return result.length === 1 ? result[0] : result;
        
    } catch (e) {
        console.error("SimpleHtmlParser Error:", e);
        return html; // Fallback to returning string? Or null?
    }
};

